import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, Button } from '../components';
import { FolderOpen, Calendar, FileText, Plus, MapPin } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import type { EmployerLocation, EmployerDepartment } from '../components/EmployerOrgStructuur';

export const EmployerDossiersPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const [searchParams] = useSearchParams();
  const professionalId = searchParams.get('professional_id');
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // Dossier taggen met vestiging/afdeling: alleen de hoofdgebruiker (Toegangs-Scope-beheer)
  const isHoofdgebruiker = user?.role === 'opdrachtgever';

  // Zelfde query-keys als EmployerOrgStructuur (backend levert alleen actieve)
  const { data: locations = [] } = useQuery({
    queryKey: ['employer-locations'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
    enabled: isHoofdgebruiker
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['employer-departments'],
    queryFn: async () => (await api.get('/employer/departments')).data?.data as EmployerDepartment[],
    enabled: isHoofdgebruiker
  });

  // Get active connections (professionals)
  const { data: connections } = useQuery({
    queryKey: ['employer-connections'],
    queryFn: async () => {
      const response = await api.get('/employer/connections');
      return response.data.data || [];
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['employer-dossiers', professionalId],
    queryFn: async () => {
      const params = professionalId ? { professional_id: professionalId } : {};
      const response = await api.get('/employer/dossiers', { params });
      return response.data.data || [];
    },
  });

  const createDossier = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/employer/dossiers', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('cockpit.dossierCreated', 'Dossier succesvol aangemaakt'));
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('cockpit.dossierCreateError', 'Fout bij aanmaken dossier'));
    }
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('common:actions.loading', 'Laden...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{t('dossiers.loadError', 'Er is een fout opgetreden bij het laden van de dossiers.')}</p>
        </div>
      </div>
    );
  }

  const dossiers = data || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dossiers.title', 'Klantendossiers')}</h1>
          <p className="mt-1 text-gray-600">{t('dossiers.subtitle', 'Beheer dossiers per professional')}</p>
        </div>
        <div className="flex gap-3">
          {professionalId && (
            <Link to="/employer/dossiers">
              <Button variant="outline">{t('dossiers.allDossiers', 'Alle Dossiers')}</Button>
            </Link>
          )}
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('dossiers.newDossier', 'Nieuw Dossier')}
          </Button>
        </div>
      </div>

      {showForm && (
        <DossierForm
          t={t as any}
          professionals={connections?.filter((c: any) => c.status === 'active') || []}
          onSubmit={(data) => createDossier.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {dossiers.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FolderOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('dossiers.noDossiers', 'Geen dossiers')}
            </h2>
            <p className="text-gray-600">
              {t('dossiers.noDossiersDesc', 'Er zijn nog geen dossiers aangemaakt.')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dossiers.map((dossier: any) => (
            <Card key={dossier.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <FolderOpen className="w-8 h-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="font-semibold font-mono text-gray-900">{dossier.dossier_code}</h3>
                    <p className="text-sm text-gray-500">
                      {dossier.professional_first_name} {dossier.professional_last_name}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>
                      {t('dossiers.startDate', 'Start')}: {new Date(dossier.start_date).toLocaleDateString('nl-NL')}
                    </span>
                  </div>
                  {dossier.end_date && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>
                        {t('dossiers.endDate', 'Eind')}: {new Date(dossier.end_date).toLocaleDateString('nl-NL')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      dossier.status === 'pending_acceptance'
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : dossier.status === 'afgewezen'
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : dossier.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : dossier.status === 'closed'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {dossier.status === 'pending_acceptance' ? t('dossiers.statusPendingAcceptance', 'Wacht op acceptatie')
                        : dossier.status === 'afgewezen' ? t('dossiers.statusRejected', 'Afgewezen')
                        : dossier.status === 'active' ? t('dossiers.statusActive', 'Actief')
                        : dossier.status === 'closed' ? t('dossiers.statusClosed', 'Gesloten')
                        : dossier.status}
                    </span>
                  </div>
                  {dossier.status === 'pending_acceptance' && (
                    <p className="text-xs text-amber-700 mt-1">{t('dossiers.waitingForProfessional', 'Wacht op acceptatie door professional')}</p>
                  )}
                  {dossier.status === 'afgewezen' && dossier.acceptance_notes && (
                    <p className="text-xs text-red-600 mt-1">{t('dossiers.explanation', 'Toelichting')}: {dossier.acceptance_notes}</p>
                  )}

                  {(dossier.max_conversations !== null && dossier.max_conversations !== undefined) && (
                    <div className="text-xs text-gray-600">
                      {t('dossiers.conversations', 'Gesprekken')}: {Number(dossier.conversation_count || 0)}/{Number(dossier.max_conversations)}
                    </div>
                  )}

                  {(dossier.approval_required === true) && (
                    <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                      {t('dossiers.approvalRequired', 'Goedkeuring nodig voor extra afspraken.')}
                    </div>
                  )}
                </div>

                {isHoofdgebruiker && (
                  <DossierScopeEditor dossier={dossier} locations={locations} departments={departments} />
                )}

                <Link to={`/employer/dossiers/${dossier.id}/verslagen`} className="w-full">
                  <Button className="w-full">
                    <FileText className="w-4 h-4 mr-2" />
                    {t('dossiers.viewReports', 'Bekijk Verslagen')}
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/** Vestiging/afdeling-tag van één dossier (voedt de Toegangs-Scope van lijnmanagers). */
const DossierScopeEditor: React.FC<{
  dossier: { id: string; location_id?: string | null; department_id?: string | null };
  locations: EmployerLocation[];
  departments: EmployerDepartment[];
}> = ({ dossier, locations, departments }) => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [locationId, setLocationId] = useState(dossier.location_id || '');
  const [departmentId, setDepartmentId] = useState(dossier.department_id || '');

  const saveScope = useMutation({
    mutationFn: async () => {
      const res = await api.patch(`/employer/dossiers/${dossier.id}/scope`, {
        location_id: locationId || null,
        department_id: departmentId || null,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('dossiers.scopeSaved'));
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers-cockpit'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('dossiers.scopeSaveError'));
    }
  });

  const naamVan = (id: string | null | undefined, lijst: Array<{ id: string; naam: string }>) =>
    id ? (lijst.find((x) => x.id === id)?.naam || t('dossiers.scopeUnknown')) : null;
  const vestiging = naamVan(dossier.location_id, locations);
  const afdeling = naamVan(dossier.department_id, departments);
  const getagd = !!(vestiging || afdeling);

  // Hybride matrix-koppeling: bij gekozen vestiging alleen universele + eigen exclusieve afdelingen
  const selectableDepartments = departments.filter(
    (d) => !locationId || !d.location_id || d.location_id === locationId
  );

  const startEdit = () => {
    setLocationId(dossier.location_id || '');
    setDepartmentId(dossier.department_id || '');
    setEditing(true);
  };

  return (
    <div className="mb-4 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center text-sm text-gray-600 min-w-0">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="truncate">
            <span className="text-gray-500">{t('dossiers.scopeLabel')}: </span>
            {getagd
              ? [vestiging, afdeling].filter(Boolean).join(' · ')
              : <span className="text-amber-700">{t('dossiers.scopeNotTagged')}</span>}
          </span>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            className="text-xs text-blue-600 hover:underline flex-shrink-0"
          >
            {t('dossiers.scopeEdit')}
          </button>
        )}
      </div>
      {!getagd && !editing && (
        <p className="text-xs text-gray-500 mt-1">{t('dossiers.scopeUntaggedHint')}</p>
      )}
      {editing && (
        <div className="mt-2 space-y-2">
          <select
            value={locationId}
            onChange={(e) => {
              const nieuw = e.target.value;
              const dep = departments.find((d) => d.id === departmentId);
              if (dep?.location_id && nieuw && dep.location_id !== nieuw) setDepartmentId('');
              setLocationId(nieuw);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            aria-label={t('dossiers.scopeLabel')}
          >
            <option value="">{t('dossiers.scopeNoLocation')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.naam}</option>
            ))}
          </select>
          <select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            aria-label={t('dossiers.scopeLabel')}
          >
            <option value="">{t('dossiers.scopeNoDepartment')}</option>
            {selectableDepartments.map((dep) => (
              <option key={dep.id} value={dep.id}>
                {dep.naam}{dep.location_naam ? ` (${dep.location_naam})` : ''}
              </option>
            ))}
          </select>
          <div className="flex gap-2 justify-end">
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
              {t('dossiers.scopeCancel')}
            </Button>
            <Button type="button" size="sm" loading={saveScope.isPending} onClick={() => saveScope.mutate()}>
              {t('dossiers.scopeSave')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const DossierForm: React.FC<{
  t: (key: string, defaultValue?: string) => string;
  professionals: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ t, professionals, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    professional_id: '',
    client_name: '',
    client_address: '',
    client_email: '',
    client_phone: '',
    description: '',
    visible_to_employer: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dossiers.createDossierTitle', 'Nieuw Dossier Aanmaken')}</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.professionalLabel', 'Professional')} *
          </label>
          <select
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.professional_id}
            onChange={(e) => setFormData({ ...formData, professional_id: e.target.value })}
          >
            <option value="">{t('cockpit.selectProfessional', 'Selecteer professional')}</option>
            {professionals.map((prof: any) => (
              <option key={prof.professional_id} value={prof.professional_id}>
                {prof.first_name} {prof.last_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('cockpit.clientName', 'Klantnaam')} *
          </label>
          <input
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.clientAddress', 'Klantadres')}
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_address}
            onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('dossiers.clientEmail', 'Klant E-mail')}
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_email}
              onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('dossiers.clientPhone', 'Klant Telefoon')}
            </label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_phone}
              onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.descriptionLabel', 'Beschrijving')}
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common:actions.cancel', 'Annuleren')}
          </Button>
          <Button type="submit">
            {t('dossiers.createDossierButton', 'Dossier Aanmaken')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
